import deepEqual from 'react-fast-compare'
import { SegmentModel } from '../../models/segments/segment.model'
import { OrgSegmentModel } from '../../models/segments/org-segment.model'
import { stripUndefined } from '../../_utils/strip-undefined'

/**
 * @module segment-query-builder/segment-dto
 *
 * The request bodies a segment save sends, and the shapes they take.
 *
 * A create sends the whole segment; an update sends only what differs from what was loaded, because
 * the API treats an absent key as "leave this alone" and a present one as an instruction. So a field
 * the reader never touched must not appear — including `channels`, where a stored segment may hold
 * none at all and an absent list has to read as empty rather than be dereferenced.
 */

export interface ISegmentCreateDto {
    name: string
    source: string
    filters_json: any
    icon_url?: string | null
    channels?: string[]
}

export interface IOrgSegmentCreateDto extends ISegmentCreateDto {
    domain_ids: number[]
}

export interface ISegmentUpdateDto {
    id: number
    name?: string
    filters_json?: any
    icon_url?: string | null
    channels?: string[]
}

export interface IOrgSegmentUpdateDto extends ISegmentUpdateDto {
    domain_ids: number[]
}

export function getSegmentCreateDto(segment: SegmentModel): ISegmentCreateDto
export function getSegmentCreateDto(segment: OrgSegmentModel, domainIds: number[]): IOrgSegmentCreateDto
export function getSegmentCreateDto(segment: OrgSegmentModel | SegmentModel, domainIds?: number[]) {
    return stripUndefined<ISegmentCreateDto | IOrgSegmentCreateDto>({
        name: segment.getName(),
        source: segment.getSource(),
        filters_json: segment.getFiltersJson(),
        icon_url: segment.getIconUrl(),
        channels: segment.getChannels(),
        domain_ids: domainIds,
    })
}

export function getSegmentUpdateDto(segment: SegmentModel, original: SegmentModel): ISegmentUpdateDto
export function getSegmentUpdateDto(
    segment: OrgSegmentModel,
    original: OrgSegmentModel,
    domainIds: number[],
): IOrgSegmentUpdateDto
export function getSegmentUpdateDto(
    segment: OrgSegmentModel | SegmentModel,
    original: OrgSegmentModel | SegmentModel,
    domainIds?: number[],
): ISegmentUpdateDto | IOrgSegmentUpdateDto {
    const dto = {} as ISegmentUpdateDto | IOrgSegmentUpdateDto

    const name = segment.getName().trim()
    if (name !== (original.getName()?.trim?.())) {
        dto.name = name
    }

    const iconUrl = segment.getIconUrl()?.trim?.() ?? null
    if (iconUrl !== (original.getIconUrl()?.trim?.() ?? null)) {
        dto.icon_url = iconUrl
    }

    const filters = segment.getFiltersJson()
    if (!deepEqual(filters, original.getFiltersJson())) {
        dto.filters_json = filters
    }

    if (Object.keys(dto).length) {
        dto.id = segment.getId()
    }

    const channels = segment.getChannels()
    if (channels) {
        // A stored segment can hold no channels at all, so the record's list may be
        // absent. Read it as empty rather than dereferencing it: giving a
        // previously channel-less segment a selection is a real change, and the diff
        // below is what has to notice it.
        const currChannels = (original.getChannels() ?? []).sort()
        const nextChannels = channels.sort()

        if (!deepEqual(nextChannels, currChannels)) {
            dto.channels = nextChannels
        }
    }

    if (domainIds) {
        const nextDomainIds = Array.from(domainIds)
        nextDomainIds.sort()
        const currDomainIds = (original as OrgSegmentModel).getDomainIds()
        currDomainIds.sort()

        if (!deepEqual(nextDomainIds, currDomainIds)) {
            const orgDto = dto as IOrgSegmentUpdateDto
            orgDto.domain_ids = nextDomainIds
        }
    }

    return stripUndefined<ISegmentUpdateDto | IOrgSegmentUpdateDto>(dto)
}
